import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Card from '../common/Card';
import Button from '../common/Button';
import Input from '../common/Input';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency } from '../../utils/formatters';
import '../../styles/subscription.css';

const SubscriptionRenew = () => {
  const navigate = useNavigate();
  const { user, loadUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [phone, setPhone] = useState('');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [payStatus, setPayStatus] = useState('pending');
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [paymentCheckError, setPaymentCheckError] = useState('');
  const [pendingLookupError, setPendingLookupError] = useState('');
  const [receiptCode, setReceiptCode] = useState('');
  const [submittingReceipt, setSubmittingReceipt] = useState(false);
  const checkingRef = useRef(false);
  const pollRef = useRef(null);

  const selectedPlan = plans.find((plan) => Number(plan.id) === Number(selectedPlanId));
  const selectedPrice = selectedPlan
    ? Number(billingPeriod === 'yearly' ? selectedPlan.price_yearly : selectedPlan.price_monthly) || 0
    : 0;
  const trialHasEnded =
    user?.subscription_status !== 'trial' ||
    !user?.trial_ends_at ||
    new Date(user.trial_ends_at) < new Date();

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const response = await api.get('/subscription.php/plans');
        const availablePlans = response.data.plans || [];
        setPlans(availablePlans);
        if (availablePlans.length) setSelectedPlanId(Number(availablePlans[0].id));

        try {
          const pendingResponse = await api.get('/subscription.php/pending-payment');
          if (pendingResponse.data.payment) {
            const pendingPayment = {
              ...pendingResponse.data.payment,
              instructions: pendingResponse.data.instructions,
              existing_payment: true
            };
            setPaymentInfo(pendingPayment);
            setPayStatus(pendingPayment.status || 'pending');
            setShowPaymentDetails(true);
            startPolling(pendingPayment.id);
          }
        } catch (error) {
          console.error('Failed to check for an existing subscription payment:', error);
          setPendingLookupError(
            'We could not check for an existing payment. Please refresh or contact support before starting another payment.'
          );
        }
      } catch (error) {
        console.error('Failed to load subscription plans:', error);
        toast.error(error.response?.data?.error || 'Failed to load subscription plans');
      } finally {
        setLoading(false);
      }
    };

    loadPlans();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const checkPaymentStatus = async (paymentId) => {
    if (checkingRef.current) return;
    checkingRef.current = true;
    setCheckingPayment(true);
    setPaymentCheckError('');
    try {
      const response = await api.get(`/subscription.php/payment-status/${paymentId}`);
      const payment = response.data.payment;
      const status = payment?.status;
      setPayStatus(status || 'pending');
      if (status === 'completed') {
        stopPolling();
        toast.success('Payment received — your subscription is active');
        try {
          if (typeof loadUser === 'function') await loadUser();
        } catch (error) {
          console.error('Could not refresh account after payment:', error);
        }
        navigate('/settings/subscription', { replace: true });
      } else if (status === 'failed' || status === 'cancelled') {
        stopPolling();
        toast.error(payment?.result_desc || 'Payment failed');
      }
    } catch (error) {
      console.error('Failed to check plan payment status:', error);
      setPaymentCheckError(
        error.response?.data?.error || 'Could not check payment status. Please try again.'
      );
    } finally {
      checkingRef.current = false;
      setCheckingPayment(false);
    }
  };

  const startPolling = (paymentId) => {
    stopPolling();
    checkPaymentStatus(paymentId);
    pollRef.current = setInterval(() => checkPaymentStatus(paymentId), 4000);
  };

  const submitReceiptCode = async () => {
    if (!paymentInfo || !receiptCode.trim()) {
      toast.error('Enter the M-Pesa receipt code');
      return;
    }

    setSubmittingReceipt(true);
    try {
      const response = await api.post('/subscription.php/submit-receipt', {
        payment_id: paymentInfo.id,
        receipt: receiptCode.trim()
      });
      setPaymentInfo((current) => ({
        ...current,
        submitted_receipt: receiptCode.trim().toUpperCase()
      }));
      toast.success(response.data.message || 'Receipt submitted for verification');
      await checkPaymentStatus(paymentInfo.id);
    } catch (error) {
      console.error('Failed to submit M-Pesa receipt:', error);
      toast.error(error.response?.data?.error || 'Could not submit the M-Pesa receipt');
    } finally {
      setSubmittingReceipt(false);
    }
  };

  const handleCheckout = async (method) => {
    if (pendingLookupError) {
      toast.error('Check for an existing payment before starting another payment');
      return;
    }
    if (!selectedPlan) {
      toast.error('Choose a subscription plan');
      return;
    }
    if (!selectedPlan.modules?.length) {
      toast.error('This plan has no apps configured. Please contact support.');
      return;
    }
    if (selectedPrice <= 0) {
      toast.error(`The ${billingPeriod} price is not available for this plan`);
      return;
    }
    if (method === 'stk' && !phone.trim()) {
      toast.error('Enter the M-Pesa phone number to receive a payment prompt');
      return;
    }

    setProcessing(true);
    try {
      const response = await api.post('/subscription.php/subscribe-plan', {
        plan_id: Number(selectedPlan.id),
        billing_period: billingPeriod
      });

      if (!response.data.payment_required) {
        toast.success(response.data.message || 'Plan activated successfully');
        if (typeof loadUser === 'function') await loadUser();
        navigate('/settings/subscription');
        return;
      }

      const payment = {
        ...response.data.payment,
        instructions: response.data.instructions,
        existing_payment: response.data.existing_payment === true
      };
      setPaymentInfo(payment);
      setPayStatus('pending');
      setShowPaymentDetails(true);

      if (payment.existing_payment) {
        startPolling(payment.id);
        toast.error('An invoice is already awaiting confirmation. Do not pay again if you already paid it.');
        return;
      }

      if (method === 'manual') {
        startPolling(payment.id);
        toast.success('Use the Paybill details below to complete your subscription');
        return;
      }

      try {
        const stkResponse = await api.post('/subscription.php/stk-pay', {
          payment_id: payment.id,
          phone: phone.trim()
        });
        startPolling(payment.id);
        toast.success(stkResponse.data.message || 'Check your phone for the M-Pesa prompt');
      } catch (error) {
        console.error('Plan payment prompt failed:', error);
        startPolling(payment.id);
        toast.error(error.response?.data?.error || 'Could not send the prompt. You can pay manually below.');
      }
    } catch (error) {
      console.error('Failed to create plan payment:', error);
      toast.error(error.response?.data?.error || 'Failed to start subscription');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="subscription-renew subscription-plan-selection">
      <div className="renew-header">
        <div>
          <h1>{trialHasEnded ? 'Your trial has ended' : 'Choose your subscription plan'}</h1>
          <p className="plans-subtitle">
            {trialHasEnded
              ? 'Choose a subscription plan and pay to continue using Harvyq. Your data is safe.'
              : 'Choose a subscription plan to continue using Harvyq after your trial.'}
          </p>
        </div>
        <Button variant="secondary" onClick={() => navigate('/subscription-expired')}>
          Back
        </Button>
      </div>

      {loading ? (
        <Card><p>Loading available plans…</p></Card>
      ) : plans.length === 0 ? (
        <Card>
          <p>No subscription plans are currently available. Please contact support for assistance.</p>
        </Card>
      ) : (
        <>
          {pendingLookupError && (
            <div className="renew-payment-status" role="alert">
              {pendingLookupError}
            </div>
          )}
          {!(paymentInfo && payStatus === 'pending') && (
            <>
          <div className="billing-period-options" role="group" aria-label="Billing period">
            <button
              type="button"
              className={billingPeriod === 'monthly' ? 'selected' : ''}
              onClick={() => setBillingPeriod('monthly')}
            >
              Monthly
            </button>
            <button
              type="button"
              className={billingPeriod === 'yearly' ? 'selected' : ''}
              onClick={() => setBillingPeriod('yearly')}
            >
              Yearly
            </button>
          </div>

          <div className="plans-grid renewal-plans-grid">
            {plans.map((plan) => {
              const planPrice = Number(
                billingPeriod === 'yearly' ? plan.price_yearly : plan.price_monthly
              ) || 0;
              const isSelected = Number(plan.id) === Number(selectedPlanId);
              const includedModules = plan.modules || [];

              return (
                <Card
                  key={plan.id}
                  className={`plan-card renewal-plan-card ${isSelected ? 'selected' : ''}`}
                >
                  <div className="plan-header">
                    <h3>{plan.plan_name}</h3>
                    <div className="plan-price">
                      <span className="amount">
                        {planPrice > 0 ? formatCurrency(planPrice) : 'Unavailable'}
                      </span>
                      <span className="period">/{billingPeriod === 'yearly' ? 'year' : 'month'}</span>
                    </div>
                  </div>
                  <div className="plan-features">
                    <h4>Apps included</h4>
                    {includedModules.length ? (
                      <ul>
                        {includedModules.map((module) => (
                          <li key={module.id}>{module.icon || '✓'} {module.module_name}</li>
                        ))}
                      </ul>
                    ) : (
                      <p>This plan is not configured with apps yet.</p>
                    )}
                    <p className="plan-limits">
                      Up to {plan.max_users} users · {Number(plan.max_products) > 0 ? `Up to ${plan.max_products} products` : 'Unlimited products'}
                    </p>
                  </div>
                  <Button
                    variant={isSelected ? 'primary' : 'secondary'}
                    fullWidth
                    disabled={!includedModules.length || planPrice <= 0}
                    onClick={() => {
                      setSelectedPlanId(Number(plan.id));
                      setPaymentInfo(null);
                      setShowPaymentDetails(false);
                      stopPolling();
                    }}
                  >
                    {isSelected ? 'Plan selected' : 'Choose this plan'}
                  </Button>
                </Card>
              );
            })}
          </div>

          {selectedPlan && (
            <Card className="renew-checkout-card">
              <h2>Continue with {selectedPlan.plan_name}</h2>
              <p>
                Amount due {billingPeriod === 'yearly' ? 'per year' : 'per month'}:{' '}
                <strong>{selectedPrice > 0 ? formatCurrency(selectedPrice) : 'Unavailable'}</strong>
              </p>
              <Input
                label="M-Pesa phone number"
                type="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                placeholder="07XXXXXXXX"
                hint="Used only if you choose an instant payment prompt."
              />
              <div className="renew-payment-buttons">
                <Button
                  variant="primary"
                  loading={processing}
                  disabled={processing || !!pendingLookupError || selectedPrice <= 0 || !selectedPlan.modules?.length}
                  onClick={() => handleCheckout('stk')}
                  fullWidth
                >
                  Pay & Continue with M-Pesa
                </Button>
                <Button
                  variant="secondary"
                  disabled={processing || !!pendingLookupError || selectedPrice <= 0 || !selectedPlan.modules?.length}
                  onClick={() => handleCheckout('manual')}
                  fullWidth
                >
                  Pay manually with M-Pesa
                </Button>
              </div>
            </Card>
          )}
            </>
          )}

          {showPaymentDetails && paymentInfo && (
            <>
              <div className="renew-payment-status" role="status">
                Payment status: <strong>{payStatus}</strong>
                {payStatus === 'pending' ? ' — waiting for payment confirmation…' : ''}
                {payStatus === 'pending' && (
                  <div className="renew-payment-check">
                    <p className="payment-check-error">
                      If you have already paid this invoice, <strong>do not pay again</strong>. Check the
                      status below; if it remains pending, contact support with your M-Pesa receipt number.
                    </p>
                    <p>
                      Manual Paybill payments update after M-Pesa sends confirmation. You can check again
                      here; keep this page open while confirmation is pending.
                    </p>
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={checkingPayment}
                      disabled={checkingPayment}
                      onClick={() => checkPaymentStatus(paymentInfo.id)}
                    >
                      Check payment status
                    </Button>
                    {paymentCheckError && <p className="payment-check-error">{paymentCheckError}</p>}
                    <div className="renew-receipt-submission">
                      {paymentInfo.submitted_receipt ? (
                        <p>
                          Receipt <strong>{paymentInfo.submitted_receipt}</strong> submitted. A platform
                          administrator must verify it before your subscription is activated.
                        </p>
                      ) : (
                        <>
                          <Input
                            label="Already paid? Enter your M-Pesa receipt code"
                            value={receiptCode}
                            onChange={(event) => setReceiptCode(event.target.value.toUpperCase())}
                            placeholder="e.g. QAB12CDE34"
                            autoComplete="off"
                          />
                          <Button
                            variant="secondary"
                            size="sm"
                            loading={submittingReceipt}
                            disabled={submittingReceipt || !receiptCode.trim()}
                            onClick={submitReceiptCode}
                          >
                            Submit receipt for verification
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="manual-payment-instructions">
                <h4>M-Pesa Paybill payment details</h4>
                <p>
                  Plan: <strong>{paymentInfo.plan_name}</strong> ({paymentInfo.billing_period})
                </p>
                <p>Invoice: <strong>{paymentInfo.invoice_number}</strong></p>
                <p className="help-text">
                  Only complete these steps if you have not already paid this invoice.
                </p>
                <ol>
                  <li>Open M-Pesa and select <strong>Lipa na M-Pesa</strong>, then <strong>Pay Bill</strong>.</li>
                  <li>Business number: <strong>{paymentInfo.paybill_number || 'Not available'}</strong></li>
                  <li>Account number: <strong>{paymentInfo.account_reference || 'Not available'}</strong></li>
                  <li>Amount: <strong>{formatCurrency(paymentInfo.amount)}</strong></li>
                  <li>Enter your M-Pesa PIN and confirm the payment.</li>
                </ol>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default SubscriptionRenew;
